import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { auth } from '../../';
import { db } from '../../';
import { curseWords } from '../../../../utils/curseWords';

export const registerUser = async (
  username,
  email,
  password,
  setIsLoading,
  navigate,
  setUsernameError,
  setEmailError,
  setPasswordError,
  setUsernameIsTaken,
  setUsernameIsUnavailable,
  setEmailIsUsed,
) => {
  // Reset errors
  setUsernameError(false);
  setEmailError(false);
  setPasswordError(false);
  setUsernameIsTaken(false);
  setUsernameIsUnavailable(false);
  setEmailIsUsed(false);

  const lowerCaseUsername = username.toLowerCase();

  // Validate username
  if (
    lowerCaseUsername.length < 6 ||
    lowerCaseUsername.length > 20 ||
    !/^[a-zA-Z0-9_]+$/.test(lowerCaseUsername)
  ) {
    setUsernameError(true);
    return;
  }

  // Validate email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    setEmailError(true);
    return;
  }

  // Validate password
  if (password.length < 8 || password.length > 25) {
    setPasswordError(true);
    return;
  }

  try {
    setIsLoading(true);
    const usersRef = collection(db, 'users');

    // Check if username is already taken
    const usernameQuery = query(
      usersRef,
      where('username', '==', lowerCaseUsername),
    );
    const usernameSnapshot = await getDocs(usernameQuery);

    if (!usernameSnapshot.empty) {
      setUsernameIsTaken(true);
      return;
    }

    // Check if username is a curse word
    if (curseWords.includes(username)) {
      setUsernameIsUnavailable(true);
      return;
    }

    // Remove the email check here - let Firebase Auth handle it

    const sessionToken = uuidv4();
    localStorage.setItem('sessionToken', sessionToken);

    // Create the auth user FIRST
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const results = userCredential.user;

    // Only create Firestore document if auth user creation succeeded
    try {
      await setDoc(doc(db, 'users', results.uid), {
        uid: results.uid,
        username: lowerCaseUsername,
        email,
        highScore: 0,
        highScoreToday: 0,
        membershipStatus: 'Basic',
        trialCredits: 50,
        sessionToken,
      });
    } catch (firestoreError) {
      // If Firestore creation fails, delete the auth user to maintain consistency
      console.error(
        'Firestore creation failed, cleaning up auth user:',
        firestoreError,
      );
      await results.delete();
      throw firestoreError;
    }

    await sendEmailVerification(results);
    await auth.signOut();

    toast.success(
      'A verification email has been sent to your email address! Please verify your email to login.',
      {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      },
    );
    navigate('/login');
  } catch (error) {
    console.error('Error during signup:', error);
    if (error.code === 'auth/email-already-in-use') {
      setEmailIsUsed(true);
    } else if (error.code === 'auth/invalid-email') {
      setEmailError(true);
    }
  } finally {
    setIsLoading(false);
  }
};

export const loginUserWithEmailAndPassword = async (
  email,
  password,
  setIsLoading,
  navigate,
) => {
  try {
    setIsLoading(true);
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const results = userCredential.user;

    // Check email verification
    if (results.emailVerified === false) {
      // Allow them to request a new verification email
      toast.warning(
        'Your email is not verified. We just sent you a new verification email!',
        {
          position: 'top-right',
          autoClose: 7000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        },
      );

      // Send a new verification email
      try {
        await sendEmailVerification(results);
        console.log('Verification email sent to:', results.email);
      } catch (emailError) {
        // Handle rate limiting or other email errors
        if (emailError.code === 'auth/too-many-requests') {
          toast.info(
            'Verification email already sent recently. Please check your inbox and spam folder.',
            {
              position: 'top-right',
              autoClose: 7000,
            },
          );
        } else {
          console.error('Error sending verification email:', emailError);
        }
      }

      // Sign them out and don't let them proceed
      await auth.signOut();
      return;
    }

    // Generate and store session token
    const sessionToken = uuidv4();
    localStorage.setItem('sessionToken', sessionToken);

    // Update Firestore with new session token
    try {
      await setDoc(
        doc(db, 'users', results.uid),
        { sessionToken },
        { merge: true },
      );
    } catch (firestoreError) {
      console.error(
        'Failed to update session token in Firestore:',
        firestoreError,
      );
      // Don't block login if Firestore update fails, but log it
      // The session token in localStorage will still work for this session
    }

    // Navigate to flashcards on successful login
    navigate('/');
  } catch (error) {
    console.error('Login error:', error);

    // Provide specific error messages based on error code
    let errorMessage = 'Invalid email or password.';

    if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email format.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email.';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Invalid email or password.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed login attempts. Please try again later.';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your connection.';
    } else if (error.code === 'auth/invalid-credential') {
      errorMessage = 'Invalid email or password.';
    }

    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });
  } finally {
    setIsLoading(false);
  }
};
